import "../Login/Login.css";
import "../Feed/style.css";

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, api, errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import StatusMessage, { errorStatus } from "../StatusMessage/StatusMessage";

const FIELDS = [
  { name: "first_name", label: "First name", autoComplete: "given-name" },
  { name: "last_name", label: "Last name", autoComplete: "family-name" },
  { name: "username", label: "Username", autoComplete: "username" },
  { name: "email", label: "Email", type: "email", autoComplete: "email" },
  {
    name: "password1",
    label: "Password",
    type: "password",
    autoComplete: "new-password",
  },
  {
    name: "password2",
    label: "Confirm password",
    type: "password",
    autoComplete: "new-password",
  },
];

const EMPTY_FORM = Object.fromEntries(FIELDS.map((f) => [f.name, ""]));

// Django form errors: { field: [{ message, code }], __all__: [...] }
function fieldMessages(errors, name) {
  return (errors?.[name] ?? []).map((e) => e.message);
}

function InviteStatus({ title, children }) {
  return (
    <div className="lefcolumn">
      <div className="header">
        <h2>{title}</h2>
      </div>
      <div className="card">{children}</div>
    </div>
  );
}

/** Public sign-up page an invite link opens. */
export default function Join() {
  const { inviteCode } = useParams();
  const { status, session, setSession, logout } = useAuth();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState(null);
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    api
      .get(`/family/api/invite_code/${inviteCode}/`)
      .then(setInvite)
      .catch((err) =>
        setLoadError(
          err instanceof ApiError && err.status === 404
            ? "not_found"
            : errorMessage(err),
        ),
      );
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFieldErrors(null);
    setMessage(null);
    try {
      setSession(await api.post(`/family/api/register/${inviteCode}/`, null, form));
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.data?.errors) {
        setFieldErrors(err.data.errors);
      } else {
        setMessage(errorStatus(errorMessage(err, "Sign up failed.")));
      }
      setIsSubmitting(false);
    }
  };

  if (loadError === "not_found") {
    return (
      <InviteStatus title="Invite not found">
        <p>This invite link isn't valid. Ask the person who sent it for a new one.</p>
      </InviteStatus>
    );
  }

  if (loadError) {
    return (
      <InviteStatus title="Something went wrong">
        <p>{loadError}</p>
      </InviteStatus>
    );
  }

  if (!invite) {
    return (
      <div className="status-container">
        <p>Loading…</p>
      </div>
    );
  }

  if (invite.member) {
    return (
      <InviteStatus title="Invite already used">
        <p>
          This invite has already been used. If it was yours,{" "}
          <Link to="/login">log in</Link> instead.
        </p>
      </InviteStatus>
    );
  }

  if (status === "authenticated") {
    return (
      <InviteStatus title="You're already logged in">
        <p>
          You're logged in as {session.member.first_name}. To sign up a new
          member with this invite, log out first.
        </p>
        <button type="button" className="interaction-button" onClick={logout}>
          Log out
        </button>{" "}
        <Link to="/">Back to the feed</Link>
      </InviteStatus>
    );
  }

  const familyNames = invite.families.map((f) => f.surname).join(", ");

  return (
    <>
      <h1>Join the family</h1>
      <div className="lefcolumn">
        <div className="header">
          <h2>You've been invited to: {familyNames}</h2>
        </div>
        <div className="card">
          <div className="login">
            <form onSubmit={handleSubmit}>
              {FIELDS.map((field) => (
                <label key={field.name}>
                  <p>{field.label}</p>
                  <input
                    name={field.name}
                    type={field.type ?? "text"}
                    autoComplete={field.autoComplete}
                    value={form[field.name]}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    required={field.name !== "email"}
                    disabled={isSubmitting}
                  />
                  {fieldMessages(fieldErrors, field.name).map((text) => (
                    <span key={text} className="error-message">
                      {text}
                    </span>
                  ))}
                </label>
              ))}
              {fieldMessages(fieldErrors, "__all__").map((text) => (
                <p key={text} className="error-message">
                  {text}
                </p>
              ))}
              <StatusMessage message={message} />
              <div>
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Joining…" : "Join"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
